import { updateState, withDevtools } from "@angular-architects/ngrx-toolkit";
import { inject } from "@angular/core";
import { DriverDto } from "@dtos/driver.dto";
import { patchState, signalStore, withComputed, withMethods, withState } from "@ngrx/signals";
import { DriversProvider } from "../providers/driversProviders/drivers.provider";
import { Helpers } from "@utils/helpers";
import { environment } from "src/environments/environment";
import { computed } from "@angular/core";
import { QualityProvider } from "@providers2/quality.provider";
import { QualityStore } from "./quality.state";

export class DriversState {
  driver: DriverDto = new DriverDto();
  curImage: File | null = null;
  errors: string[] = [];
}

export const DriverStore = signalStore(
  {
    providedIn: 'root',
    // Configuración para Redux DevTools
    ...(typeof window !== 'undefined' && (window as any).__REDUX_DEVTOOLS_EXTENSION__ && {
      devtools: {
        name: 'RoadIt Driver Store',
        trace: true,
        traceLimit: 25
      }
    })
  },
  withDevtools('DriverStore'),
  // Estado inicial usando directamente DriversState
  withState(new DriversState()),
  withComputed((store) => ({
    imageSrc: computed(() => {
      const curImage: any = store.curImage();
      if (curImage instanceof File) {
        return URL.createObjectURL(curImage);
      }
      if (store.driver().image && store.driver().image!.length > 0) {
        let image = store.driver().image!;
        if (!image?.startsWith("http") && !image?.startsWith("blob:")) {
          return environment.apiUrl + image;
        }
        return image;
      }
      return null;
    })
  })),
  // Métodos globales
  withMethods((store) => {
    return {
      updateState: (updates: Partial<DriversState>) => {
        patchState(store, { ...updates });
        saveDriverStoreToStorage(store);
      },
      initializeFromStorage: () => {
        const loadedStore = loadDriverStoreFromStorage();
        if (loadedStore) {
          patchState(store, loadedStore);
        }
      },
      destroy: () => {
        patchState(store, new DriversState());
      }
    }
  }),
  // Métodos que acceden a providers
  withMethods((store) => {
    const prov = inject(DriversProvider);
    const qualityStore = inject(QualityStore);
    return {
      clearStore: () => {
        store.updateState(new DriversState());
      },

      loadById: async (driverId: number, withIncidents: boolean = false) => {
        try {
          const driverData = await prov.getDriverById(driverId);
          if (!driverData) {
            console.error('No se pudo cargar el conductor');
            return;
          }
          // Deserializar correctamente usando fromJson para ejecutar constructores
          
          const driver = DriverDto.fromJson(driverData);
          
          store.updateState({
            driver,
            curImage: null,
            errors: []
          });
          if(withIncidents){
            qualityStore.loadIncidents({driver: driverId}).then((res) => {
              console.log(res);
            });
          }
        } catch (error) {
          store.updateState({
            driver: new DriverDto(),
            curImage: null,
            errors: ['Error al cargar conductor por ID']
          });
          console.error('Error al cargar conductor por ID:', error);
          return;
        }
      },

      create: async (driverData: Partial<DriverDto>, imageFile?: File | null): Promise<DriverDto | null> => {
        try {
          const docs = store.driver().documents;
          delete driverData.validated;
          const createdDriver = await prov.createDriver(driverData, imageFile);
          if (!createdDriver) {
            console.error('No se pudo crear el conductor');
            return null;
          }
          // Deserializar correctamente usando fromJson para ejecutar constructores
          const driver = DriverDto.fromJson(createdDriver);
          // ACTUALIZAR EL ESTADO CON EL CONDUCTOR CREADO
          store.updateState({
            driver: { ...driver.toJson(), documents: docs },
            curImage: imageFile || null,
            errors: []
          });
          return driver;
        } catch (error) {
          console.error('Error al crear conductor:', error);
          store.updateState({
            errors: ['Error al crear conductor']
          });
          return null;
        }
      },

      update: async (driverId: number, driverData: Partial<DriverDto>, imageFile?: File | null): Promise<DriverDto | null> => {
        try {
          const docs = store.driver().documents;
          delete driverData.validated;
          const updatedDriver = await prov.updateDriver(driverId, driverData, imageFile);
          if (!updatedDriver) {
            console.error('No se pudo actualizar el conductor');
            return null;
          }

          // Deserializar correctamente usando fromJson para ejecutar constructores

          const driver = DriverDto.fromJson(updatedDriver);
          // ACTUALIZAR EL ESTADO CON EL CONDUCTOR ACTUALIZADO
          store.updateState({
            driver: { ...driver.toJson(), documents: docs },
            curImage: imageFile || null,
            errors: []
          });
          return driver;
        } catch (error) {
          console.error('Error al actualizar conductor:', error);
          store.updateState({
            errors: ['Error al actualizar conductor']
          });
          return null;
        }
      },

      delete: async (driverId: number): Promise<boolean> => {
        try {
          const success = await prov.deleteDriver(driverId);
          if (!success) {
            console.error('No se pudo eliminar el conductor');
            return false;
          }
          return true;
        } catch (error) {
          console.error('Error al eliminar conductor:', error);
          return false;
        }
      }
    }
  })
);

function saveDriverStoreToStorage(store: any, key = 'roadit_driver_store') {
  const result: Record<string, unknown> = {};
  for (const prop of Object.keys(new DriversState())) {
    result[prop] = store[prop]();
  }
  Helpers.setSessionStorage(key, result);
}

// Restaura solo las claves de DriversState, asumiendo que todas son signals
function loadDriverStoreFromStorage(key = 'roadit_driver_store'): DriversState | null {
  return Helpers.getSessionStorage<DriversState>(key);
}
